import { FC, useCallback, useEffect, useRef } from 'react';
import { DndContext, useDraggable, DragEndEvent } from '@dnd-kit/core';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { usePortal } from '@frontend/design-system';
import { useChatbotAssistantConfig } from '../../hooks/use-chatbot-assistant-config';
import { ChatbotAssistantContext, useChatbotAssistant } from '../../provider/chatbot-assistant.provider';
import { TriggerButton } from '../trigger-button';
import { ChatbotWidget } from './chatbot-widget';

export const AIOnboardingAssistant: FC = () => {
  const chatbotAssistantConfig = useChatbotAssistantConfig();
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const { position, setPosition } = chatbotAssistantConfig;

  useEffect(() => {
    if (position === null && triggerButtonRef.current) {
      const { bottom, left } = triggerButtonRef.current.getBoundingClientRect();
      setPosition({
        x: left,
        y: bottom + 16,
      });
    }
  }, [position, setPosition]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { delta } = event;

      setPosition({
        x: (position?.x ?? 0) + delta.x,
        y: (position?.y ?? 0) + delta.y,
      });
    },
    [setPosition, position]
  );

  return (
    <ChatbotAssistantContext.Provider value={chatbotAssistantConfig}>
      <TriggerButton innerRef={triggerButtonRef} />
      <DndContext onDragEnd={handleDragEnd}>
        <DraggableChatbotWidget />
      </DndContext>
    </ChatbotAssistantContext.Provider>
  );
};

const DraggableChatbotWidget: FC = () => {
  const { position } = useChatbotAssistant();

  const { Portal } = usePortal({
    attributes: { id: 'ai-onboarding-assistant' },
  });

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: 'ai-onboarding-assistant',
  });

  return (
    <Portal>
      <div
        ref={setNodeRef}
        css={styles.draggableBox({
          x: (position?.x ?? 0) + (transform?.x ?? 0),
          y: (position?.y ?? 0) + (transform?.y ?? 0),
          isDragging,
        })}
      >
        <ChatbotWidget
          dragHandle={
            <div {...listeners} {...attributes} css={styles.dragHandle}>
              <Icon name='drag-handle' color='white' />
            </div>
          }
        />
      </div>
    </Portal>
  );
};

const styles = {
  draggableBox: ({ x, y, isDragging }: { x: number; y: number; isDragging: boolean }) => css`
    position: fixed;
    left: ${x}px;
    top: ${y}px;
    transition: transform 0.2s ease;
    ${isDragging && 'transform: scale(1.05);'}
  `,
  dragHandle: css`
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  `,
};
